import type { Bounds } from '@/modules/Editor/models/raster/Raster'
import type { PartialRecord } from '@/core/helperTypes'
import type { VideoBounds } from '@/modules/Editor/models/raster/VideoRaster'
import { combineBounds } from '@/modules/Editor/plugins/mask/utils/shared/combineBounds'

export type RasterFrameData = {
  index: number
  buffer: Uint8Array
  labels: Set<number>
  bounds: VideoBounds
}

export class RasterFrames {
  private _frames: PartialRecord<number, RasterFrameData> = {}

  // computed helpers
  get frames(): RasterFrameData[] {
    return Object.values(this._frames) as RasterFrameData[]
  }

  get frameIndexes(): number[] {
    return Object.keys(this._frames).map((frameIndexString) => Number(frameIndexString))
  }

  get frameCount(): number {
    return Object.keys(this._frames).length
  }

  get labels(): number[] {
    const labels: Set<number> = new Set()
    for (const frame of this.frames) {
      if (!frame) continue
      for (const label of frame.labels) {
        labels.add(label)
      }
    }
    return Array.from(labels)
  }

  get framesWithLabels(): number[] {
    const frames: number[] = []
    for (const frame of this.frames) {
      if (frame.labels.size > 0) {
        frames.push(frame.index)
      }
    }
    return frames
  }

  /**
   * Computes the raster range
   *
   * IMPORTANT: This uses all frame indexes, not just frames with labels.
   * This ensures that new frames are included in the range even before they have labels,
   * which is necessary for proper rendering when creating new mask annotations on empty frames.
   *
   * Previously, this only used framesWithLabels, which caused getActiveBufferForRender()
   * to return undefined for new frames, preventing mask rendering until the frame was
   * changed and changed back.
   */
  get range(): [number, number] {
    const allFrameIndexes = this.frameIndexes

    // Use all frame indexes, not just frames with labels
    // This ensures new frames are included in the range even before they have labels
    return allFrameIndexes.length > 0
      ? [Math.min(...allFrameIndexes), Math.max(...allFrameIndexes)]
      : [0, 0]
  }

  bufferAt(frameIndex: number): Uint8Array | undefined {
    return this._frames[frameIndex]?.buffer
  }

  setBufferAt(frameIndex: number, buffer: Uint8Array): void {
    this._frames[frameIndex] = this._frames[frameIndex] || this.createFrame(frameIndex)
    this._frames[frameIndex].buffer = buffer
  }

  labelsOnFrame(frameIndex: number): number[] {
    if (!this._frames[frameIndex]) {
      return []
    }
    return Array.from(this._frames[frameIndex].labels)
  }

  keyframesWithLabel(labelIndex: number): number[] {
    const keyframes: number[] = []
    Object.keys(this._frames).forEach((frameIndexString) => {
      const frameIndex = Number(frameIndexString)
      if (this._frames[frameIndex]?.labels.has(labelIndex)) {
        keyframes.push(Number(frameIndex))
      }
    })
    return keyframes
  }

  /**
   * returns a map with all label bounds per frame index
   */
  getLabelBounds(labelIndex: number): VideoBounds {
    const videoBounds: VideoBounds = {}
    Object.keys(this._frames).forEach((frameIndexString) => {
      const frame = this._frames[Number(frameIndexString)]
      if (!frame) {
        return
      }
      if (frame.bounds[labelIndex] === undefined) {
        return
      }
      videoBounds[Number(frameIndexString)] = frame.bounds[labelIndex]
    })
    return videoBounds
  }

  /**
   * returns the combine bounds of multiple labels from all frames where they are present.
   */
  getLabelsBounds(labelIndexes: number[]): Bounds | undefined {
    const bounds: Bounds[] = []

    labelIndexes.forEach((labelIndex: number) => {
      const videoBounds = this.getLabelBounds(labelIndex)
      Object.values(videoBounds).forEach((frameBounds) => bounds.push(frameBounds))
    })

    return combineBounds(bounds)
  }

  createFrame(frameIndex: number): RasterFrameData {
    return {
      index: frameIndex,
      buffer: new Uint8Array(),
      labels: new Set(),
      bounds: {},
    }
  }

  setLabelAt(frameIndex: number, labelIndex: number, bounds: Bounds): void {
    this._frames[frameIndex] = this._frames[frameIndex] || this.createFrame(frameIndex)
    this._frames[frameIndex].labels.add(labelIndex)
    this._frames[frameIndex].bounds = {
      ...this._frames[frameIndex].bounds,
      [labelIndex]: bounds,
    }
  }

  hasLabelAt(frameIndex: number): boolean {
    if (!this._frames[frameIndex]) {
      return false
    }
    return this._frames[frameIndex].labels.size > 0
  }

  removeLabelAt(frameIndex: number, labelIndex: number): void {
    const frame = this._frames[frameIndex]
    if (!frame) {
      return
    }
    frame.labels.delete(labelIndex)
    delete frame.bounds[labelIndex]

    if (frame.labels.size === 0) {
      delete this._frames[frameIndex]
    }
  }

  removeLabelsAt(frameIndex: number, labelIndexes?: number[]): void {
    if (labelIndexes) {
      labelIndexes.forEach((labelIndex) => this.removeLabelAt(frameIndex, labelIndex))

      if (!this.hasLabelAt(frameIndex)) {
        this.removeKeyframe(frameIndex)
      }

      return
    }

    // if no label indexes are passed, then we need to remove all labels from the frame, hence
    // delete the frame
    this.removeKeyframe(frameIndex)
  }

  removeLabel(labelIndex: number): void {
    this.frameIndexes.forEach((frameIndex: number) => this.removeLabelAt(frameIndex, labelIndex))
  }

  removeKeyframe(frameIndex: number): void {
    delete this._frames[frameIndex]
  }

  clear(): void {
    this._frames = {}
  }
}
